import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import BannerSlide from "./BannerSlide";

/**
 * O slider de campanhas do topo da home.
 *
 * Promoção, anúncio e destaque de campanha — artes que a loja cadastra em
 * Admin → Banners. É a primeira coisa que a pessoa vê, então ele carrega a
 * responsabilidade de não atrapalhar: passa sozinho, mas para de passar assim
 * que alguém demonstra interesse em ler.
 *
 * ── Conteúdo que se move sozinho ───────────────────────────────────────────
 * Carrossel automático é um problema conhecido de acessibilidade: ele troca o
 * conteúdo debaixo de quem está lendo. A WCAG 2.2.2 exige um jeito de parar, e
 * aqui isso aparece em quatro camadas, da mais silenciosa para a mais explícita:
 *
 *   1. NÃO COMEÇA se o sistema pede menos movimento (prefers-reduced-motion).
 *      Quem sofre com animação não deveria precisar clicar em nada.
 *   2. PARA no hover e no foco — ler ou tabular já é sinal de interesse.
 *   3. PARA com a aba em segundo plano, para não gastar bateria animando o que
 *      ninguém está vendo, e para a pessoa não voltar num slide diferente.
 *   4. BOTÃO de pausa visível, que é a exigência formal e a única camada que
 *      funciona para quem simplesmente quer que aquilo pare.
 *
 * ── Por que rolagem nativa e não transform ─────────────────────────────────
 * `scroll-snap` dá o arrastar do dedo de graça, mantém o slider utilizável se o
 * JS falhar e torna o trilho alcançável por teclado. Um carrossel em `transform`
 * precisaria reimplementar os três.
 */

const INTERVALO = 6000;

export default function HeroSlider({ banners }) {
  const trilho = useRef(null);
  const [atual, setAtual] = useState(0);
  // Começa tocando, mas `menosMovimento` decide se o relógio chega a existir.
  const [tocando, setTocando] = useState(true);
  const [interagindo, setInteragindo] = useState(false);

  const menosMovimento = typeof window !== "undefined"
    && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  const total = banners.length;

  const irPara = useCallback((i, suave = true) => {
    const el = trilho.current;
    if (!el) return;
    // O índice dá a volta: no último, "próximo" retorna ao primeiro. Num slider
    // de campanha isso é o esperado — não há "fim" a comunicar, ao contrário da
    // vitrine de produtos, onde o fim da lista é uma informação real.
    const destino = ((i % total) + total) % total;
    el.scrollTo({ left: destino * el.clientWidth, behavior: suave && !menosMovimento ? "smooth" : "auto" });
  }, [total, menosMovimento]);

  /* O índice vem da POSIÇÃO da rolagem, e não de um contador próprio: com um
     contador, arrastar o dedo deixaria o ponto marcando um slide e a tela
     mostrando outro. Aqui a rolagem é a fonte da verdade, e o dedo e o botão
     alimentam a mesma. */
  const medir = () => {
    const el = trilho.current;
    if (el && el.clientWidth) setAtual(Math.round(el.scrollLeft / el.clientWidth));
  };

  useEffect(() => {
    if (menosMovimento || !tocando || interagindo || total < 2) return;

    const relogio = setInterval(() => {
      // `document.hidden` a cada tique, e não um listener de visibilidade: é uma
      // leitura barata e dispensa desmontar e remontar o relógio na troca de aba.
      if (!document.hidden) irPara(atual + 1);
    }, INTERVALO);

    return () => clearInterval(relogio);
  }, [atual, tocando, interagindo, total, menosMovimento, irPara]);

  if (!total) return null;

  const seta = "absolute top-1/2 -translate-y-1/2 z-20 hidden md:inline-flex items-center justify-center h-11 w-11 rounded-full bg-white/90 backdrop-blur text-slate-800 shadow-lg ring-1 ring-slate-900/10 hover:bg-white hover:scale-105 active:scale-95 transition-all";

  return (
    <section
      aria-roledescription="carrossel"
      aria-label="Promoções e novidades"
      className="relative bg-slate-900"
      onMouseEnter={() => setInteragindo(true)}
      onMouseLeave={() => setInteragindo(false)}
      onFocusCapture={() => setInteragindo(true)}
      onBlurCapture={() => setInteragindo(false)}
    >
      <ul
        ref={trilho}
        onScroll={medir}
        data-testid="hero-slider"
        /* Região rolável precisa ser alcançável pelo teclado (WCAG 2.1.1). */
        // eslint-disable-next-line
        tabIndex={0}
        className="flex overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {banners.map((b, i) => {
          /* O visual sai do BannerSlide, que a PRÉVIA do admin também usa.
             Duas marcações parecidas divergiriam na primeira mudança, e a
             prévia só serve enquanto for exata. Aqui fica o que é do
             carrossel: o link, a medida do slide e a rolagem. */
          const Conteudo = <BannerSlide banner={b} eager={i === 0} />;

          const medida = "relative snap-start shrink-0 w-full aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/9] overflow-hidden";
          const externo = b.link?.startsWith("http");

          return (
            <li key={b.id} className={medida}>
              {/* Banner sem link não vira <a>: link que não navega é pior que
                  nenhum, e some do Tab de quem navega por teclado. */}
              {!b.link ? (
                <div className="group block w-full h-full">{Conteudo}</div>
              ) : externo ? (
                <a href={b.link} target="_blank" rel="noreferrer" className="group block w-full h-full" data-testid="hero-slide-link">{Conteudo}</a>
              ) : (
                <Link to={b.link} className="group block w-full h-full" data-testid="hero-slide-link">{Conteudo}</Link>
              )}
            </li>
          );
        })}
      </ul>

      {total > 1 && (
        <>
          <button type="button" onClick={() => irPara(atual - 1)}
            aria-label="Banner anterior" data-testid="hero-prev" className={`${seta} left-4`}>
            <ChevronLeft size={22} aria-hidden />
          </button>
          <button type="button" onClick={() => irPara(atual + 1)}
            aria-label="Próximo banner" data-testid="hero-next" className={`${seta} right-4`}>
            <ChevronRight size={22} aria-hidden />
          </button>

          <div className="absolute bottom-4 inset-x-0 z-20 flex items-center justify-center gap-3">
            <div className="flex gap-2" data-testid="hero-dots">
              {banners.map((b, i) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => irPara(i)}
                  aria-label={`Ir para o banner ${i + 1} de ${total}`}
                  aria-current={i === atual ? "true" : undefined}
                  data-testid="hero-dot"
                  className="h-6 px-0.5 flex items-center group"
                >
                  {/* Contorno escuro porque o ponto pousa sobre foto, que pode
                      ser clara: só o branco desapareceria em metade das artes. */}
                  <span className={`block h-2 rounded-full ring-1 ring-slate-900/20 transition-all duration-300 ${
                    i === atual ? "w-7 bg-white" : "w-2 bg-white/55 group-hover:bg-white/80"}`} />
                </button>
              ))}
            </div>

            {/* Só existe quando há o que pausar: sem movimento automático, um
                botão de pausa é um controle que não controla nada. */}
            {!menosMovimento && (
              <button
                type="button"
                onClick={() => setTocando((v) => !v)}
                aria-label={tocando ? "Pausar a troca automática de banners" : "Retomar a troca automática de banners"}
                data-testid="hero-playpause"
                className="ml-1 inline-flex items-center justify-center h-7 w-7 rounded-full bg-slate-900/45 text-white/90 ring-1 ring-white/25 hover:bg-slate-900/70 transition-colors"
              >
                {tocando ? <Pause size={12} aria-hidden /> : <Play size={12} aria-hidden />}
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}
